/**
 * Sortable column header for the homescreen tables (redesign § Table behavior): a quiet
 * `.th-sort` button whose tiny sort arrow renders only on the active column, direction-aware.
 * `aria-sort` sits on the `<th>` so assistive tech hears the current order.
 */

import { SortArrowIcon } from '../../components';

export type SortDir = 'asc' | 'desc';

export type SortState<K extends string> = { key: K; dir: SortDir };

export type SortableHeaderProps<K extends string> = {
  label: string;
  columnKey: K;
  sort: SortState<K>;
  onSort: (key: K) => void;
  /** Native hover text — how an abbreviated header ("ER", "/wk") says its full name. */
  title?: string;
  className?: string;
};

export function SortableHeader<K extends string>({
  label,
  columnKey,
  sort,
  onSort,
  title,
  className,
}: SortableHeaderProps<K>) {
  const active = sort.key === columnKey;
  return (
    <th
      className={className}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="th-sort" title={title} onClick={() => onSort(columnKey)}>
        {label}
        {active ? (
          <span className="th-sort__arrow" aria-hidden="true">
            <SortArrowIcon dir={sort.dir} />
          </span>
        ) : null}
      </button>
    </th>
  );
}

export default SortableHeader;
